// Shared with CinematicArena and LiveArena. Extracted so both surfaces
// consume the exact same state machine — outcome is unaffected by which
// renderer runs.

export const TAP_COST_ATTACK = 3;
export const TAP_COST_RESULT = 1;
export const ULTIMATE_TAP_THRESHOLD = 60;
export const CONSUMABLE_FREEZE_MS = 1600;
export const COMBO_TIERS = [10, 25, 50, 100];

export function buildEventQueue(battle) {
  const q = [];
  const rounds = battle?.rounds || [];
  rounds.forEach((rd, ri) => {
    (rd.log || []).forEach((line) => {
      const kind = classify(line);
      q.push({ round: rd.round ?? ri + 1, line, kind,
               tap_cost: kind === 'attack' ? TAP_COST_ATTACK : TAP_COST_RESULT });
    });
    q.push({ round: rd.round ?? ri + 1, kind: 'round_end',
             hp_after: rd.hp_after, status_after: rd.status_after,
             abilities: rd.abilities, winner: rd.winner, tap_cost: 0 });
  });
  q.push({ kind: 'finale', tap_cost: 0 });
  return q;
}

function classify(line) {
  const l = (line || '').toLowerCase();
  if (l.startsWith('— round')) return 'round_intro';
  if (l.includes('combo!')) return 'combo';
  if (l.startsWith('— ') && l.includes('takes the round')) return 'round_win';
  if (l.startsWith('— ')) return 'flavor';
  if (l.includes('lunges') || l.includes('strikes') || l.includes('charges') || l.includes('attacks') || l.includes('unleashes')) return 'attack';
  if (l.includes('critical') || l.includes('crushes')) return 'crit';
  if (l.includes('slips') || l.includes('dodges') || l.includes('evades') || l.includes('melts')) return 'dodge';
  if (l.includes('goes wide') || l.includes('miss')) return 'miss';
  if (l.includes('counter')) return 'counter';
  if (l.includes('frozen and')) return 'skip';
  return 'result';
}

export const INITIAL = (battle) => ({
  queue: buildEventQueue(battle),
  cursor: 0, tapEnergy: 0,
  ultimateCharge: 0, ultimateFired: false,
  comboCounter: 0, comboTierIndex: -1,
  screenShakeKey: 0, screenFlash: null,
  slowMoUntil: 0, frozenUntil: 0,
  logLines: [],
  hp: { ch: 1.0, op: 1.0 },
  status: { ch: [], op: [] },
  abilities: { ch: [], op: [] },
  currentRound: 1, finished: false,
  winnerRoundWins: null, lastLineKind: null,
});

export function reducer(state, action) {
  switch (action.type) {
    case 'INIT': return INITIAL(action.battle);
    case 'TAP': {
      if (state.finished) return state;
      if (Date.now() < state.frozenUntil) return state;
      const ev = state.queue[state.cursor];
      if (!ev) return state;
      const nextEnergy = state.tapEnergy + 1;
      const newUlt = Math.min(ULTIMATE_TAP_THRESHOLD, state.ultimateCharge + 1);
      const newCombo = state.comboCounter + 1;
      let nextTierIndex = state.comboTierIndex;
      let flash = null;
      for (let i = state.comboTierIndex + 1; i < COMBO_TIERS.length; i++) {
        if (newCombo >= COMBO_TIERS[i]) { nextTierIndex = i; flash = 'combo'; }
      }
      if (nextEnergy >= (ev.tap_cost || 1)) {
        return advance({ ...state, tapEnergy: 0, ultimateCharge: newUlt,
                          comboCounter: newCombo, comboTierIndex: nextTierIndex,
                          screenFlash: flash || state.screenFlash });
      }
      return { ...state, tapEnergy: nextEnergy, ultimateCharge: newUlt,
               comboCounter: newCombo, comboTierIndex: nextTierIndex,
               screenFlash: flash || state.screenFlash };
    }
    case 'FIRE_ULTIMATE': {
      if (state.ultimateFired || state.ultimateCharge < ULTIMATE_TAP_THRESHOLD) return state;
      let s = { ...state, ultimateFired: true, ultimateCharge: 0,
                screenFlash: 'ultimate', slowMoUntil: Date.now() + 900 };
      for (let i = 0; i < 5; i++) s = advance(s);
      return s;
    }
    case 'FAST_FORWARD': {
      let s = state;
      let safety = 500;
      while (!s.finished && s.queue[s.cursor] && safety-- > 0) s = advance(s);
      return s;
    }
    case 'CLEAR_FLASH': return { ...state, screenFlash: null };
    default: return state;
  }
}

function advance(state) {
  const ev = state.queue[state.cursor];
  if (!ev) return { ...state, finished: true };
  const nextCursor = state.cursor + 1;
  let s = { ...state, cursor: nextCursor, tapEnergy: 0, lastLineKind: ev.kind };
  if (ev.kind === 'finale') return { ...s, finished: true };
  if (ev.kind === 'round_end') {
    const total = (ev.hp_after?.challenger || 0) + (ev.hp_after?.opponent || 0) || 1;
    const hpMax = ev.hp_after ? {
      ch: ev.hp_after.challenger / total,
      op: ev.hp_after.opponent / total,
    } : s.hp;
    return { ...s, hp: hpMax,
             status: ev.status_after ? { ch: ev.status_after.challenger || [], op: ev.status_after.opponent || [] } : s.status,
             abilities: ev.abilities ? { ch: ev.abilities.challenger || [], op: ev.abilities.opponent || [] } : s.abilities,
             currentRound: (ev.round || s.currentRound) + 1 };
  }
  const flash = ev.kind === 'crit' ? 'crit'
             : ev.kind === 'dodge' ? 'dodge'
             : ev.kind === 'combo' ? 'combo' : null;
  const shake = (ev.kind === 'crit') ? state.screenShakeKey + 1 : state.screenShakeKey;
  const linesNext = [{ text: ev.line, kind: ev.kind }, ...state.logLines].slice(0, 40);
  return { ...s, screenFlash: flash || s.screenFlash, screenShakeKey: shake, logLines: linesNext };
}
